import { Col, Dropdown, Menu, message, Modal, Row, Select, Spin, Table, TableColumnsType, TableProps, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { Filter, OptionFilter, StatusApproval } from "src/entity/status/status.entity";
import {
  getApprovalById,
  getOptions,
  getSummaryApproval,
  getYear,
  resendMailApproval,
  updateApproveReport,
} from "src/services/status.service";
import { profileStore } from "src/stores/profile-store";
import { useShallow } from "zustand/react/shallow";
import { canViewTable } from "../PermissionCheck/PermissionCheck";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { CloseOutlined, EllipsisOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { TbMailDown } from "react-icons/tb";
import { TbFileCheck } from "react-icons/tb";
import { BiLinkExternal } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { ApproveBody, DataApprove, ResponseAPI } from "src/entity/voc-approve/voc-approve.entity";
import ModalApproveStep from "src/components/ModalApproveStep/ModalApproveStep";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import axios from "axios";
import { generateYearArray } from "src/utils/array";
import { companyStore } from "src/stores/company-store";
import { componentPalette } from "src/theme/component-palette";
import { PiWarningCircleLight } from "react-icons/pi";
import "./StatusTable.css";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];
const { Text } = Typography;

const StatusTable = () => {
  const navigate = useNavigate();
  const profile = profileStore(useShallow((state) => state.profile));
  const { permissions } = useNavigationMenu();
  const [messageApi, contextHolder] = message.useMessage();
  const currentYear = new Date().getFullYear();
  const [companyId, setCompanyId] = useState<string>("");
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [optionMonth, setOptionMonth] = useState<{ label: string; value: string }[]>([
    { label: "Jan", value: "jan" },
    { label: "Feb", value: "feb" },
    { label: "Mar", value: "mar" },
    { label: "Apr", value: "apr" },
    { label: "May", value: "may" },
    { label: "Jun", value: "jun" },
    { label: "Jul", value: "jul" },
    { label: "Aug", value: "aug" },
    { label: "Sep", value: "sep" },
    { label: "Oct", value: "oct" },
    { label: "Nov", value: "nov" },
    { label: "Dec", value: "dec" },
  ]);
  const [selectedMonth, setSelectedMonth] = useState<string>("jan");
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(1);
  const [filter, setFilter] = useState<Filters>({});
  const [optionsFilter, setOptionFilter] = useState<OptionFilter[]>([]);
  const [dataSummary, setDataSummary] = useState<StatusApproval[]>([]);
  const [openModalApproveStep, setOpenModalApproveStep] = useState<boolean>(false);
  const [approveData, setApproveData] = useState<DataApprove | null>(null);
  const userId = profile?.userId;
  const email = profile?.email;
  const name = profile?.name;
  const [selectedApprove, setSelectedApprove] = useState<string>();
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>("");
  const [selectedMenu, setSelectedMenu] = useState<string>("");
  const pageSize = 10;

  const getCompanyAreaDetail = companyStore(useShallow((state) => state.getCompanyAreaDetail));
  const dataCompanyAreaDetails = companyStore(useShallow((state) => state.dataCompanyAreaDetails));

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years[0] as number);
  };

  const warningAlert = (message: string | string[], action: string) => {
    const warnModal = Modal.confirm({
      title: (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Can not {action}</span>
          <CloseOutlined onClick={() => warnModal.destroy()} style={{ cursor: "pointer" }} />
        </div>
      ),
      content: message,
      okText: "OK",
      icon: <InfoCircleOutlined style={{ color: componentPalette.redThird }} />,
      okButtonProps: { style: { textAlign: "center", backgroundColor: componentPalette.primary } },
      cancelButtonProps: { style: { display: "none" } },
      onOk: () => {},
      className: "custom-tmp-modal-warning",
    });
  };

  const handleApproveReport = async (company: string, menuName: string, approveBody: ApproveBody, action: string) => {
    try {
      setLoading(true);
      const response = await updateApproveReport(company, menuName, approveBody);
      setLoading(false);
      setOpenModalApproveStep(false);
      if (response.data.code == 200) {
        if (action === "approved") {
          messageApi.open({
            type: "success",
            content: CommonSuccess.APPROVED_SUCCESSFULL,
          });
        } else if (action === "reject") {
          messageApi.open({
            type: "success",
            content: CommonSuccess.REJECT_SUCCESSFULL,
          });
        }
        fetchSummaryApproval();
      } else {
        messageApi.open({
          type: "error",
          content: CommonError.APPROVED_FAIL,
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const responseData = error?.response?.data as ResponseAPI;
        const actionStr = action === "approved" ? "approve" : "reject";
        if (error?.response?.status === 400) {
          warningAlert(String(responseData.message), actionStr);
        } else {
          messageApi.open({
            type: "error",
            content: CommonError.APPROVED_FAIL,
          });
        }
      }
      console.error(error);
    } finally {
      setLoading(false);
      setSelectedApprove("");
      setSelectedCompany("");
      setSelectedCompanyName("");
      setOpenModalApproveStep(false);
    }
  };

  const handleYearChange = (value: number) => {
    setSelectedYear(value);
  };

  const handleMonthChange = (value: string) => {
    setSelectedMonth(value);
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let queryParam = "";
    if (selectedYear) {
      queryParam += `year=${selectedYear}`;
    }
    if (selectedMonth) {
      queryParam += `&month=${selectedMonth}`;
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchOption = async () => {
    try {
      if (selectedYear && selectedMonth) {
        const query = `year=${selectedYear}&month=${selectedMonth}`;
        const response = await getOptions(query);
        // console.log("response", response);
        if (response.data.code === 200) {
          const data = response.data.data.filter;
          // console.log("data", data);
          setOptionFilter(data);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaryApproval = async () => {
    try {
      if (companyId && selectedYear) {
        setLoading(true);
        let offset = 0;
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }
        const query = createParamFilter();
        const response = await getSummaryApproval(query);
        if (response.data.code === 200) {
          const data = response.data.data;
          let summary = data.map((el: any, index: number) => {
            return {
              no: (index + offset + 1).toString(),
              ...el,
            };
          });
          if (canViewTable(permissions)) {
            // console.log("summary", summary);
            setDataSummary([...summary]);
          } else {
            setDataSummary([]);
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resendMail = async (approveId: string, menuName: string) => {
    try {
      setLoading(true);
      const payload = {
        menuName,
        approveId,
      };
      setLoading(true);
      const response = await resendMailApproval(payload);
      if (response.data.code === 200) {
        messageApi.open({
          type: "success",
          content: CommonSuccess.SENDMAIL_SUCCESSFULL,
        });
      }
    } catch (error) {
      console.error(error);
      messageApi.open({
        type: "success",
        content: CommonError.SOMETHING_WRONG,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchInfoStepApprove = async (approveId: string, menuName: string) => {
    try {
      setLoading(true);
      const query = `approveId=${approveId}&menuName=${menuName}`;
      const response = await getApprovalById(query);
      if (response.data.code === 200) {
        setLoading(false);
        const approveInfo = response.data.data;
        setApproveData(approveInfo);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = optionsFilter.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  useEffect(() => {
    fetchYear();
    getCompanyAreaDetail();
  }, []);

  useEffect(() => {
    if (profile?.activeCompany._id) {
      setCompanyId(profile?.activeCompany._id);
      fetchOption();
      if (profile?.activeCompany) {
        const fetchData = async () => {
          const companyId = profile.activeCompany._id;
          setCompanyId(companyId);
          setSelectedYear(currentYear);
        };
        fetchData();
      }
    }
  }, []);

  useEffect(() => {
    fetchSummaryApproval();
  }, [filter, selectedYear, selectedMonth]);

  useEffect(() => {
    fetchOption();
  }, [selectedYear, selectedMonth]);

  const renderTag = (text: any, record: any, key: string) => {
    return tagRender(text?.status, text?._id, key, record?.companyId, record?.companyName, text?.year, text?.month, text?.approveNo);
  };

  const columns: TableColumnsType<StatusApproval> = [
    {
      title: "No.",
      dataIndex: "no",
    },
    {
      title: "Company",
      dataIndex: "companyName",
      filters: mapFilter("company"),
    },
    {
      title: "Fugitive",
      dataIndex: "fugitive",
      render: (text, record) => renderTag(text, record, "fugitive"),
    },
    {
      title: "Combustion",
      dataIndex: "combustion",
      render: (text, record) => renderTag(text, record, "combustion"),
    },
    {
      title: "Flare",
      dataIndex: "flare",
      render: (text, record) => renderTag(text, record, "flare"),
    },
    {
      title: "Transport",
      dataIndex: "transport",
      render: (text, record) => renderTag(text, record, "transport"),
    },
    {
      title: "Tank",
      dataIndex: "tank",
      render: (text, record) => renderTag(text, record, "tank"),
    },
    {
      title: "Wastewater",
      dataIndex: "wasteWater",
      render: (text, record) => renderTag(text, record, "wasteWater"),
    },
    {
      title: "Others",
      dataIndex: "others",
      render: (text, record) => renderTag(text, record, "others"),
    },
  ];

  const mapStatusStep = [
    {
      label: "Processing",
      value: "processing",
    },
    {
      label: "Waiting App 1",
      value: "request",
    },
    {
      label: "Waiting App 2",
      value: "approver_1",
    },
    {
      label: "Waiting App 3",
      value: "approver_2",
    },
    {
      label: "Approved",
      value: "approver_3",
    },
    {
      label: "Rejected",
      value: "reject",
    },
  ];

  const comfirmResendMail = (approveId: string, menuName: string) => {
    Modal.confirm({
      title: "Do you want to send E-Mail?",
      className: "custom-modal",
      okText: "Yes",
      cancelText: "No",
      icon: <PiWarningCircleLight size={32} className="custom-modal-icon" />,
      okButtonProps: { style: { backgroundColor: componentPalette.primary } },
      cancelButtonProps: { style: { borderColor: componentPalette.primary, color: componentPalette.primary } },
      onOk: () => {
        resendMail(approveId, menuName);
      },
    });
  };

  const handleMenuClick = async (
    approveId: string,
    menuName: string,
    companyId: string,
    companyName: string,
    year: number,
    month: string,
    approveNo: number,
    e: any,
  ) => {
    if (e.key === "1") {
      // Navigate page Approve
      let pathMenu = menuName;
      if (menuName === "transport") {
        pathMenu = "transportation-and-marketing";
      } else if (menuName === "wasteWater") {
        pathMenu = "waste-water-treatment";
      }

      let reportPathName = `report-voc-emission`;
      if (menuName === "others") {
        reportPathName = `report`;
      }
      // navigate(`/vocs-inventory/${pathMenu}/report-voc-emission?companyId=${companyId}&year=${year}&month=${month}&approveNo=${approveNo}`);
      window.open(
        `/vocs-inventory/${pathMenu}/${reportPathName}?companyId=${companyId}&year=${year}&month=${month}&approveNo=${approveNo}`,
        "_blank",
      );
    } else if (e.key === "2") {
      // Call API Resend Mail
      comfirmResendMail(approveId, menuName);
      // resendMail(approveId, menuName);
    } else if (e.key === "3") {
      // Open Modal Approve Step
      await fetchInfoStepApprove(approveId, menuName);
      setOpenModalApproveStep(true);
      setSelectedApprove(approveId);
      setSelectedMenu(menuName);
      setSelectedCompany(companyId);
      setSelectedCompanyName(companyName);
    }
  };

  const tagRender = (
    status: string,
    approveId: string,
    menuName: string,
    companyId: string,
    comapnyName: string,
    year: number,
    month: string,
    approveNo: number,
  ) => {
    let tagColor = "";
    let borderColor = "";
    let textColor = "";
    switch (status) {
      case "processing":
        tagColor = "#DBF8FF";
        borderColor = "#2DB1EABA";
        textColor = "#2DB1EA";
        break;
      case "request":
        tagColor = "#E6F5FF";
        borderColor = "#2C58EA";
        textColor = "#2C58EA";
        break;
      case "approver_1":
        tagColor = "#FFF7E6";
        borderColor = "#FFD591";
        textColor = "#FA8C16";
        break;
      case "approver_2":
        tagColor = "#FFF7E6";
        borderColor = "#EED51A";
        textColor = "#FACC16";
        break;
      case "approver_3":
        tagColor = "#F6FFED";
        borderColor = "#B7EB8F";
        textColor = "#52C41A";
        break;
      case "reject":
        tagColor = "#FFBFBF";
        borderColor = "#D83D0E";
        textColor = "#D83D0E";
        break;
      default:
        tagColor = "default";
        break;
    }
    const labelStatus = mapStatusStep.find((item) => item.value === status);
    const isDisabled = status === "processing" ? true : false;
    const isDisabledResendMail = ["processing", "reject", "approver_3"].includes(status) ? true : false;
    const menu = (
      <Menu onClick={(e) => handleMenuClick(approveId, menuName, companyId, comapnyName, year, month, approveNo, e)}>
        <Menu.Item key="1" disabled={isDisabled}>
          <Row align={"middle"}>
            <BiLinkExternal style={{ marginRight: 8, fontSize: 17 }} />
            <Text disabled={isDisabled}>Report</Text>
          </Row>
        </Menu.Item>
        <Menu.Item key="2" disabled={isDisabledResendMail}>
          <Row align={"middle"}>
            <TbMailDown style={{ marginRight: 8, fontSize: 17 }} />
            <Text disabled={isDisabledResendMail}>Remind</Text>
          </Row>
        </Menu.Item>
        <Menu.Item key="3" disabled={isDisabled}>
          <Row align={"middle"}>
            <TbFileCheck style={{ marginRight: 8, fontSize: 19 }} />
            <Text disabled={isDisabled}>Approve</Text>
          </Row>
        </Menu.Item>
      </Menu>
    );
    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <Tag
          style={{
            color: textColor,
            borderColor: borderColor,
            backgroundColor: tagColor,
            width: "90px",
            textAlign: "center",
          }}
        >
          {labelStatus?.label}
        </Tag>
        {status !== "-" && (
          <Dropdown overlay={menu} trigger={["click"]}>
            <EllipsisOutlined
              style={{
                fontSize: 20,
                cursor: "pointer",
                transform: "rotate(90deg)",
                marginLeft: 8,
              }}
            />
          </Dropdown>
        )}
      </div>
    );
  };

  const onTableChange: TableProps["onChange"] = (pagination: any, filters: any) => {
    setFilter(filters);
    setCurrent(pagination?.current);
  };

  return (
    <div>
      {contextHolder}
      <Modal
        open={openModalApproveStep}
        width={1200}
        title={
          <Row style={{ display: "flex", gap: "280px", justifyContent: "center", paddingBottom: "10px", paddingLeft: "50px" }}>
            <Col>
              <strong>Company: </strong>
              <span>{selectedCompanyName}</span>
            </Col>
            <Col>
              <strong>Report: </strong>
              <span>{selectedMenu === "wasteWater" ? "Wastewater" : selectedMenu.charAt(0).toUpperCase() + selectedMenu.slice(1)}</span>
            </Col>
          </Row>
        }
        bodyStyle={{ height: "80vh", overflowY: "auto", overflowX: "auto", paddingRight: "40px" }} // Add paddingRight to avoid overlap
        onCancel={() => setOpenModalApproveStep(false)}
        footer={null}
      >
        <>
          {openModalApproveStep && approveData && userId && selectedApprove && email && name && (
            <>
              {loading ? (
                <Spin style={{ display: "flex", justifyContent: "center", alignItems: "center" }} />
              ) : (
                <ModalApproveStep
                  data={approveData}
                  userId={userId}
                  approveId={selectedApprove}
                  email={email}
                  name={name}
                  onApprove={(approveBody, action) => {
                    handleApproveReport(String(selectedCompany), String(selectedMenu), approveBody, action);
                  }}
                />
              )}
            </>
          )}
        </>
      </Modal>
      <div style={{ display: "flex", paddingBottom: "10px" }}>
        <div style={{ paddingRight: "20px" }}>
          <Select
            value={selectedYear}
            style={{ width: 190 }}
            onChange={handleYearChange}
            options={optionYears.map((year) => ({ label: year, value: year }))}
            loading={loading}
            size="middle"
          />
        </div>
        <div>
          <Select
            value={selectedMonth}
            style={{ width: 190 }}
            onChange={handleMonthChange}
            options={optionMonth}
            loading={loading}
            size="middle"
          />
        </div>
      </div>
      <Table columns={columns} dataSource={dataSummary} pagination={false} onChange={onTableChange} loading={loading} />
    </div>
  );
};

export default StatusTable;
